import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import { useTimerVolumeContext } from "@contexts/TimerVolumeContext";
import { useUserContext } from "@contexts/UserContext";
import SettingsIcon from "@icons/SettingsGearIcon.svg";
import { useAppModeContext } from "../../../../../app/LifeAtApp/AppModeContext";
import { taskTimerState$ } from "../../../../task-management/TaskTimerGlobals";
import { PersonalPomodoroService } from "../../../service/PersonalPomodoroService";
import { getAlarmSound } from "./PomodoroUtil";
import TaskTimerPlaceholder from "./TaskTimerPlaceholder";
import TaskTimerSettings from "./TaskTimerSettings";
import TimerAudioComponent from "./TimerAudioComponent";
import TimerCountdownAndActionsRow from "./TimerCountdownAndActionsRow";
import { PAUSE_TIMER_LABEL } from "./TimerWidgetConstants";
import { useAlarmSoundOptions } from "./hooks/useAlarmSoundOptions";
import { useTaskTimer } from "./hooks/useTaskTimer";

const StyledPomodoroTimer = styled.div<{ hidden: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    padding: 10px 0;

    background: ${({ theme }) => theme.backgroundPrimary};
    color: ${({ theme }) => theme.textColorPrimary};

    ${({ hidden }) =>
        hidden &&
        css`
            display: none;
        `}
`;

const TimerSelections = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    width: auto;
`;

const StyledSettingsIcon = styled(SettingsIcon)`
    fill: ${({ theme }) => theme.iconColor};
`;

interface TaskTimerProps {
    readonly hidden: boolean;
    readonly openTimerWidget: () => void;
    readonly setTimerModeToTask: () => void;
}
export default function TaskTimer(props: TaskTimerProps) {
    // Note: if the timerVolume (in saved user data) is set to 0, timerAudioVol will default to DEFAULT_TIMER_VOLUME
    const timerAudioRef: MutableRefObject<HTMLAudioElement | null> = useRef(null);

    const { setAppMode } = useAppModeContext();
    const { timerVolume, changeTimerVolume } = useTimerVolumeContext();
    const { user, userData } = useUserContext();

    const taskTimerState = taskTimerState$.value;

    const pomodoroService = PersonalPomodoroService(user, userData);
    const alarmSoundOptions = useAlarmSoundOptions(pomodoroService);
    const {
        saveSettings,
        start,
        pause,
        reset,
        isRunning,
        timerDisplayText,
        pomodoroSettingsInput,
        dispatchPomodoroSettingsInput,
    } = useTaskTimer({
        hidden: props.hidden,
        pomodoroService,
        alarmSoundOptions,
        audioRef: timerAudioRef,
    });

    useEffect(() => {
        if (taskTimerState.isTimerRunning) {
            setAppMode("focus");
            props.openTimerWidget();
            props.setTimerModeToTask();
            start();
        }
    }, [taskTimerState]);

    const toggleTimer = () => {
        if (isRunning) {
            pause();
        } else {
            start();
        }
    };

    const { selectedAlarmSound } = pomodoroSettingsInput;

    const alarmSound = useMemo(
        () => getAlarmSound(alarmSoundOptions, selectedAlarmSound),
        [alarmSoundOptions, selectedAlarmSound],
    );
    const [showSettings, setShowSettings] = useState(false);

    const toggleSettingsVisible = () => {
        setShowSettings(!showSettings);
    };

    const handleSettingsSave = () => {
        saveSettings();
        setShowSettings(false);
    };

    return (
        <StyledPomodoroTimer hidden={props.hidden}>
            {taskTimerState.isTimerRunning && taskTimerState.taskDuration ? (
                <>
                    <TimerCountdownAndActionsRow
                        timerDisplayText={timerDisplayText}
                        isRunning={isRunning}
                        stopTimerActionButtonLabel={PAUSE_TIMER_LABEL}
                        canEditSettings={false}
                        onTimerToggle={toggleTimer}
                        onTimerReset={() => reset()}
                    />
                    <TimerSelections>
                        <LifeAtButton
                            isLink={false}
                            icon={<StyledSettingsIcon />}
                            handleClick={toggleSettingsVisible}
                        />
                    </TimerSelections>
                </>
            ) : (
                <TaskTimerPlaceholder />
            )}
            <TimerAudioComponent alarmSound={alarmSound} timerVolume={timerVolume} />
            {showSettings && (
                <TaskTimerSettings
                    canEditSettings={false}
                    timerVolume={timerVolume}
                    setTimerVolume={(newVol: number) => changeTimerVolume("personal", newVol)}
                    pomodoroSettingsInput={pomodoroSettingsInput}
                    dispatch={dispatchPomodoroSettingsInput}
                    alarmSoundOptions={alarmSoundOptions}
                    pomodoroService={pomodoroService}
                    onSettingsSave={handleSettingsSave}
                />
            )}
        </StyledPomodoroTimer>
    );
}
